import { CardStackPlusIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Heading, IconButton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { AddPitchIcon } from 'components/common/custom-icon/shorthands';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PlateView } from 'components/common/plate-view';
import { PitchListSidebarControls } from 'components/sections/pitch-list/sidebar/controls';
import { PLSidebarHitterControls } from 'components/sections/pitch-list/sidebar/hitter-controls';
import { PitchListSidebarSectionTitle } from 'components/sections/pitch-list/sidebar/section-title';
import { PLSidebarVideoControls } from 'components/sections/pitch-list/sidebar/video-controls';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { PitchListDialog } from 'enums/pitch-lists';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { TrainPitchesFn, VideoChangedFn } from 'interfaces/function-types';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { GAME_STATUS_DISABLE_ACTION_MSG } from 'lib_ts/enums/mlb.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { clone } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const PRESERVE_BS_HASH = true;

interface IProps {
  // whether fire controls are shown
  enableFire?: boolean;

  // whether add to PL button at the bottom is shown
  enableAddToList?: boolean;
  // whether save as button near plate is shown
  enableSaveAs?: boolean;

  handleSave?: (pitch: IPitch) => void;

  onVideoChanged: VideoChangedFn;

  onTrainFn?: TrainPitchesFn;

  className?: string;
}

export const PitchListSidebar = (props: IProps) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const dialog = usePitchListStore((s) => s.dialog);

  const isInGame = useAuthStore((s) => s.isInGame);

  const { activeStrikeZone } = useContext(MachineContext);

  const { pitchBP, plateBP, setPlateBP } = useContext(AimingContext);

  // changing pitches updates plateBP accordingly
  useEffect(() => {
    if (!pitchBP) {
      return;
    }

    const safePlate =
      pitchBP.plate_loc_backup ?? TrajHelper.getPlateLoc(pitchBP.traj);

    setPlateBP(safePlate);
  }, [pitchBP?._id]);

  // currently necessary so that changing hitter w/ sz correctly redraws
  const plateKey = useMemo(() => {
    return MiscHelper.hashCode(
      [MiscHelper.hashify(activeStrikeZone) ?? 'no sz'].join('-')
    );
  }, [activeStrikeZone]);

  const [dialogSaveAs, setDialogSaveAs] = useState<{
    key: number;
    pitch: IPitch;
  }>();

  const getPitchForSaving = useCallback(async () => {
    if (!pitchBP) {
      return;
    }

    const safePriority = pitchBP.priority ?? BuildPriority.Spins;

    // derive the bs and traj for the pitch using the new plate location
    const derived = PitchDesignHelper.deriveBsAndTraj(
      {
        speedMph: BallHelper.getSpeedFromBs(pitchBP.bs),
        spins: pitchBP.bs,
        spinExt: BallHelper.convertSpinToSpinExt(pitchBP.bs),
        breaks: pitchBP.breaks ?? TrajHelper.getBreaks(pitchBP.traj),
        seams: pitchBP.seams ?? BallHelper.getSeamsFromBS(pitchBP.bs),
        plate: plateBP,
        releasePos: pitchBP.bs,
        videoIDs: {
          windupVideoID: pitchBP.windupVideoID,
          stretchVideoID: pitchBP.stretchVideoID,
        },
      },
      safePriority
    );

    const derivedPlate = TrajHelper.getPlateLoc(derived.traj);

    console.assert(
      derivedPlate.plate_x === plateBP.plate_x,
      `DERIVED traj plate_x (${derivedPlate.plate_x}) matches target plate_x (${plateBP.plate_x})`
    );

    console.assert(
      derivedPlate.plate_z === plateBP.plate_z,
      `DERIVED traj plate_z (${derivedPlate.plate_z}) matches target plate_z (${plateBP.plate_z})`
    );

    const output = clone(pitchBP);

    const bs_hash = MachineHelper.getBsHash(derived.bs, safePriority);

    // if the hash tail (i.e. hash of the properties) is mismatched after deriving, use the new bs_hash b/c bs changed somehow
    if (!PRESERVE_BS_HASH && !bs_hash.endsWith(pitchBP.bs_hash)) {
      // ? older bs_hash values lack the build priority prefix, so it is possible for the tail of a new-style hash to be equal to an entire old-style hash
      output.bs_hash = bs_hash;
    }

    output.bs = derived.bs;
    output.traj = derived.traj;
    output.plate_loc_backup = plateBP;

    return output;
  }, [pitchBP, plateBP]);

  // ! deprecate this if training w/ TMS doesn't cause shots to update until everything is done
  // whether training dialog is open => pause drawing shots in the plate view
  const isTraining = dialog?.mode === PitchListDialog.Training;

  const drawShots = useMemo(() => {
    if (isTraining) {
      // never draw shots while training
      return false;
    }

    if (onDemand) {
      // always draw shots for on-demand mode
      return true;
    }

    if (pitchBP?._mx_tms?._mx_trained) {
      // only draw shots if pitch is trained
      return true;
    }

    return false;
  }, [isTraining, onDemand, pitchBP?._mx_tms?._mx_trained]);

  return (
    <ErrorBoundary identifier="PitchListSidebar">
      <Flex direction="column">
        <Card
          size="2"
          data-identifier="PLSidebar"
          style={{
            width: '240px',
            alignSelf: 'flex-start',
            maxHeight: '100%',
          }}
          className={props.className}
        >
          <Flex
            style={{
              overflowY: 'auto',
              scrollbarWidth: 'none',
              height: '100%',
            }}
            direction="column"
          >
            <Box
              // for truncation
              minWidth="0"
              mb="1"
            >
              <Heading size="3" truncate>
                {pitchBP
                  ? (pitchBP.name ?? t('pl.unnamed-pitch'))
                  : t('pl.select-a-pitch')}
              </Heading>
            </Box>

            <Flex direction="column" gap="4">
              {/* plate */}
              <Flex direction="column" gap="2">
                <Flex gap={RADIX.FLEX.GAP.SM} justify="between">
                  <PitchListSidebarSectionTitle
                    label="pd.plate-location"
                    title={t('pd.batters-pov').toString()}
                    className="cursor-help"
                  />

                  {props.enableSaveAs && (
                    <Box>
                      <IconButton
                        size="1"
                        className="btn-floating"
                        title={t('common.save-as').toString()}
                        color={RADIX.COLOR.NEUTRAL}
                        variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                        disabled={!pitchBP}
                        onClick={async () => {
                          if (isInGame) {
                            NotifyHelper.warning({
                              message_md: GAME_STATUS_DISABLE_ACTION_MSG,
                            });
                            return;
                          }

                          const pitch = await getPitchForSaving();

                          if (!pitch) {
                            return;
                          }

                          setDialogSaveAs({
                            key: Date.now(),
                            pitch: pitch,
                          });
                        }}
                      >
                        <CardStackPlusIcon />
                      </IconButton>
                    </Box>
                  )}
                </Flex>

                <Box>
                  <PlateView
                    identifier="PLPlateView"
                    key={plateKey}
                    pitch={pitchBP}
                    onUpdate={(plate) => {
                      setPlateBP(plate);
                    }}
                    drawShots={drawShots}
                    disabled={isTraining || !pitchBP || !!pitchBP.debugMs}
                    useHitter
                    border
                  />
                </Box>
              </Flex>

              {/* hitter */}
              {props.enableFire && <PLSidebarHitterControls />}

              {/* video */}
              <PLSidebarVideoControls onVideoChanged={props.onVideoChanged} />

              {/* controls */}
              {props.enableFire && (
                <PitchListSidebarControls
                  identifier="PLSidebarControls"
                  onTrainFn={props.onTrainFn}
                />
              )}

              {props.enableAddToList && (
                <Button
                  disabled={!pitchBP}
                  onClick={async () => {
                    if (!props.handleSave) {
                      NotifyHelper.debug({
                        message_md:
                          'There is no handleSave callback configured!',
                      });
                      return;
                    }

                    if (isInGame) {
                      NotifyHelper.warning({
                        message_md: GAME_STATUS_DISABLE_ACTION_MSG,
                      });
                      return;
                    }

                    const pitch = await getPitchForSaving();

                    if (!pitch) {
                      return;
                    }

                    props.handleSave(pitch);
                  }}
                >
                  <AddPitchIcon /> {t('pd.add-to-pitch-list')}
                </Button>
              )}
            </Flex>
          </Flex>
        </Card>
      </Flex>

      {dialogSaveAs && (
        <CopyPitchesDialog
          key={dialogSaveAs.key}
          title={t('pd.add-to-pitch-list')}
          identifier="PitchListSidebarSavePitchDialog"
          reloadPitches={usePitchListStore.getState().reloadPitches}
          description={t('pl.complete-form-to-save-pitch-to-list').toString()}
          pitches={[dialogSaveAs.pitch]}
          onCreated={() => setDialogSaveAs(undefined)}
          onClose={() => setDialogSaveAs(undefined)}
          duplicating
        />
      )}
    </ErrorBoundary>
  );
};
